import axios, {AxiosInstance, AxiosError} from "axios";
import type {AppStore} from "../store";

// ── Расширение типов axios ──────────────────────────────────────────────────
// Добавляем кастомные поля в AxiosRequestConfig, чтобы их можно было
// передавать в конфиге запроса и читать в интерсепторах без any/приведений.
//
//   _retry — флаг, что для этого запроса уже был выполнен refresh access-токена.
//   Защищает от бесконечного цикла повторов при 401.
declare module "axios" {
    export interface AxiosRequestConfig {
        _retry?: boolean;
    }

    export interface InternalAxiosRequestConfig {
        _retry?: boolean;
    }
}

// ── HTTP-пути и инстанс axios ──────────────────────────────────────────────

export const nestServerPath = process.env.REACT_APP_NEST_SERVER_PATH ?? "/api";
export const fileServerPath = process.env.REACT_APP_FILE_SERVER_PATH ?? "/api/file/static";
export const websocketPath = process.env.REACT_APP_WEBSOCKET_PATH ?? "";

export const appAPI: AxiosInstance = axios.create({
    baseURL: nestServerPath,
    withCredentials: true,
});

// ── Ленивая инъекция store ─────────────────────────────────────────────────
// store импортируется не напрямую (иначе циклическая зависимость),
// а через injectStore(store) — вызов в index.tsx после setupStore().

let storeRef: AppStore | null = null;

export const injectStore = (store: AppStore) => {
    storeRef = store;
};

// ── Auth helpers ────────────────────────────────────────────────────────────

export async function getNewAccessToken(): Promise<string> {
    const response = await fetch(`${nestServerPath}/auth/refresh`, {
        method: "POST",
        credentials: "include",
    });
    if (!response.ok) {
        throw new Error("Не удалось обновить токен");
    }
    const data = await response.json();
    return data.access_token as string;
}

/** Обновлять заранее, если до конца жизни access-токена меньше этого (S4.3). */
export const REFRESH_AHEAD_MS = 30_000;

/** Поля `exp` / `iat` из payload JWT (секунды); не разобрать — null. Подпись не проверяется. */
const jwtTimes = (token: string): {exp: number; iat: number | null} | null => {
    try {
        const part = token.split(".")[1];
        if (!part) return null;
        const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
        const payload = JSON.parse(atob(b64)) as {exp?: unknown; iat?: unknown};
        if (typeof payload.exp !== "number") return null;
        return {exp: payload.exp, iat: typeof payload.iat === "number" ? payload.iat : null};
    } catch {
        return null;
    }
};

/**
 * Разница «часы клиента − часы сервера», мс: снимается по `iat` токена,
 * полученного обновлением. Без неё отстающие или спешащие часы клиента
 * обновляли бы токен на каждый запрос.
 */
let clockOffsetMs = 0;

/**
 * Access-токен истёк или истекает меньше чем через REFRESH_AHEAD_MS (S4.3).
 * Токен без `exp` или неразборчивый — false: пусть решает сервер (401 → обновление).
 */
export const tokenExpiresSoon = (token: string, now: number = Date.now()): boolean => {
    const times = jwtTimes(token);
    if (!times) return false;
    return times.exp * 1000 - (now - clockOffsetMs) < REFRESH_AHEAD_MS;
};

/**
 * Одно общее обновление access-токена (single-flight, S4.3): пока идёт запрос
 * к `/auth/refresh`, остальные ждут его же промис, а не шлют свой. Успех —
 * новый токен в localStorage; провал — токен удаляется, ошибка у всех ждущих.
 */
let refreshing: Promise<string> | null = null;

export function refreshAccessToken(): Promise<string> {
    if (!refreshing) {
        refreshing = getNewAccessToken()
            .then((token) => {
                localStorage.setItem("access_token", token);
                appAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
                const iat = jwtTimes(token)?.iat;
                if (iat != null) clockOffsetMs = Date.now() - iat * 1000;
                return token;
            })
            .catch((e: unknown) => {
                localStorage.removeItem("access_token");
                throw e;
            })
            .finally(() => {
                refreshing = null;
            });
    }
    return refreshing;
}

/**
 * Токен для запроса или подключения сокета: истекает — сначала общее
 * обновление (чтобы 401 не возникал вовсе). Токена нет или обновить не
 * удалось — null (запрос уйдёт без него, ответит 401, сессия кончилась).
 */
export async function getFreshAccessToken(): Promise<string | null> {
    const token = localStorage.getItem("access_token");
    if (!token) return null;
    if (!tokenExpiresSoon(token)) return token;
    try {
        return await refreshAccessToken();
    } catch {
        return null;
    }
}

/** Только для тестов: сбросить общее обновление и поправку часов. */
export const resetTokenRefreshForTests = () => {
    refreshing = null;
    clockOffsetMs = 0;
};

// ── Request interceptor: подкладывает access-токен ─────────────────────────

appAPI.interceptors.request.use(async (config) => {
    const token = await getFreshAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ── Response interceptor ────────────────────────────────────────────────────
//
// Логика:
//   1. 401 → общее обновление access-токена через refresh-cookie (single-flight),
//      затем повторяем оригинальный запрос (один раз, через _retry). Если токен
//      уже сменился, пока запрос летел, — повтор без нового обновления.
//   2. Остальное — реджектим без вмешательства.

appAPI.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError) => {
        const originalRequest = error.config;

        // ── 1. 401 → refresh ──
        const is401 = error.response?.status === 401;
        const isRefreshCall = originalRequest?.url?.includes("/auth/refresh");
        const alreadyRetried = originalRequest?._retry;
        const current = localStorage.getItem("access_token");

        if (
            is401 &&
            !isRefreshCall &&
            !alreadyRetried &&
            current &&
            originalRequest
        ) {
            originalRequest._retry = true;
            try {
                const sent = String(originalRequest.headers.get("Authorization") ?? "");
                const newToken = sent === `Bearer ${current}` ? await refreshAccessToken() : current;
                originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
                return appAPI(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        // ── 2. Прочее ──
        return Promise.reject(error);
    },
);